import { Link } from 'react-router-dom';
import { RefreshCw, ArrowLeft } from 'lucide-react';

export default function Refund() {
  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <RefreshCw className="w-8 h-8 text-accent-blue" />
        <h1 className="text-3xl font-bold text-white">Refund Policy</h1>
      </div>

      <div className="prose prose-invert max-w-none space-y-8">
        <p className="text-gray-400">
          <strong>Effective Date:</strong> January 1, 2025<br />
          <strong>Last Updated:</strong> January 26, 2025
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white">1. Overview</h2>
          <p className="text-gray-300">
            At Northcoast AI Academy, we want you to be completely satisfied with your learning experience.
            This Refund Policy outlines when and how you can request a refund for our subscription plans.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">2. Free Trial</h2>
          <p className="text-gray-300">
            We offer free access to select lessons and features. You can explore these without payment
            to ensure our platform is right for you before subscribing.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">3. Subscription Refunds</h2>

          <div className="mt-4 space-y-4">
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-accent-blue">30-Day Money-Back Guarantee</h3>
              <p className="text-gray-300 mt-2">
                New subscribers are eligible for a full refund within 30 days of their initial purchase,
                no questions asked. This applies to:
              </p>
              <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                <li>Monthly subscriptions (first month)</li>
                <li>Annual subscriptions (first 30 days)</li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white">After 30 Days</h3>
              <p className="text-gray-300 mt-2">
                <strong>Monthly Subscriptions:</strong> No refunds after the initial 30-day period.
                You can cancel anytime to prevent future charges.
              </p>
              <p className="text-gray-300 mt-2">
                <strong>Annual Subscriptions:</strong> Prorated refunds may be available for unused
                months in cases of documented hardship. Contact support for evaluation.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">4. How to Request a Refund</h2>
          <ol className="list-decimal list-inside text-gray-300 space-y-3">
            <li>
              <strong>Email Us:</strong> Send a refund request to <span className="text-accent-blue">billing@northcoast.ai</span>
            </li>
            <li>
              <strong>Include Details:</strong>
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li>Your account email address</li>
                <li>Date of purchase</li>
                <li>Reason for refund (optional but helpful)</li>
              </ul>
            </li>
            <li>
              <strong>Processing Time:</strong> We process refund requests within 5-7 business days
            </li>
            <li>
              <strong>Refund Method:</strong> Refunds are issued to the original payment method
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">5. Non-Refundable Items</h2>
          <p className="text-gray-300">
            The following are not eligible for refunds:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Subscriptions after 30 days from initial purchase (monthly plans)</li>
            <li>Accounts terminated for Terms of Service violations</li>
            <li>Purchases made fraudulently</li>
            <li>Promotional or discounted purchases (case-by-case basis)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">6. Cancellation vs. Refund</h2>
          <div className="bg-gray-800/50 rounded-lg p-4 mt-4">
            <p className="text-gray-300">
              <strong>Cancellation</strong> stops future billing but does not automatically trigger a refund.
              Your access continues until the end of your current billing period.
            </p>
            <p className="text-gray-300 mt-2">
              <strong>Refund</strong> returns your payment and may immediately revoke access,
              depending on when the refund is requested.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">7. How to Cancel</h2>
          <p className="text-gray-300">
            You can cancel your subscription at any time:
          </p>
          <ol className="list-decimal list-inside text-gray-300 space-y-2 mt-2">
            <li>Log in to your account</li>
            <li>Go to Dashboard &gt; Account Settings</li>
            <li>Click "Manage Subscription"</li>
            <li>Select "Cancel Subscription"</li>
          </ol>
          <p className="text-gray-300 mt-2">
            Or email <span className="text-accent-blue">support@northcoast.ai</span> and we'll cancel for you.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">8. Special Circumstances</h2>
          <p className="text-gray-300">
            We understand that unexpected situations arise. We may offer refunds or credits outside our
            standard policy in cases of:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2 mt-2">
            <li>Extended service outages affecting your access</li>
            <li>Documented medical emergencies</li>
            <li>Significant financial hardship</li>
            <li>Technical issues preventing platform use</li>
          </ul>
          <p className="text-gray-300 mt-2">
            Contact us to discuss your situation.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">9. Contact Information</h2>
          <p className="text-gray-300">
            For billing questions or refund requests:
          </p>
          <p className="text-gray-300 mt-2">
            <strong>Email:</strong> billing@northcoast.ai<br />
            <strong>Response Time:</strong> Within 2 business days
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white">10. Changes to This Policy</h2>
          <p className="text-gray-300">
            We may update this Refund Policy periodically. Changes will not affect refund requests
            already in process. Check this page for the latest policy.
          </p>
        </section>
      </div>
    </div>
  );
}
